// MEDFLOW - Calendar Page
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Eye, EyeOff } from 'lucide-react';
import { addMinutes, addWeeks, eachDayOfInterval, endOfWeek, format, isSameDay, startOfWeek, subWeeks } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { SyncIndicator } from '@/components/ui/SyncIndicator';
import { appointmentsApi, boxesApi, patientsApi, professionalsApi, servicesApi } from '@/lib/api';
import { isSupabaseAuthEnabled } from '@/lib/auth-config';
import { useSmartSync } from '@/hooks/useSmartSync';
import {
  createSupabaseAppointment,
  loadSupabaseAppointments,
  loadSupabaseBoxes,
  loadSupabasePatientsList,
  loadSupabaseProfessionals,
  loadSupabaseServices,
  updateSupabaseAppointment,
} from '@/lib/supabase-data';
import { cn, formatTime, getStatusColor, getStatusLabel } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

interface Professional {
  id: string;
  color: string;
  user: { name: string };
  specialty: string;
  services?: Array<{ id: string; name: string; durationMinutes: number }>;
}

interface Box {
  id: string;
  name: string;
  isActive: boolean;
}

interface PatientOption {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

interface ServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  price?: number;
}

interface Appointment {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  notes?: string;
  patient?: { name: string; phone?: string; email?: string; rut?: string };
  professional?: { color: string; user?: { name: string }; specialty?: string };
  service?: { name: string; durationMinutes?: number; price?: number };
  box?: { id: string; name: string } | null;
}

interface BookingFormValues {
  patientId: string;
  professionalId: string;
  serviceId: string;
  boxId: string;
  date: string;
  time: string;
  notes: string;
}

function Modal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: React.ReactNode }) {
  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100">
            Cerrar
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function BookingModal({
  open,
  patients,
  professionals,
  services,
  boxes,
  onClose,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  patients: PatientOption[];
  professionals: Professional[];
  services: ServiceOption[];
  boxes: Box[];
  onClose: () => void;
  onSubmit: (values: BookingFormValues) => void;
  isLoading: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<BookingFormValues>({
    patientId: '',
    professionalId: '',
    serviceId: '',
    boxId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    notes: '',
  });

  const filteredProfessionals = useMemo(() => {
    if (!form.serviceId) {
      return professionals;
    }

    return professionals.filter((professional) => {
      if (!professional.services || professional.services.length === 0) {
        return true;
      }

      return professional.services.some((service) => service.id === form.serviceId);
    });
  }, [form.serviceId, professionals]);

  const selectedService = useMemo(() => services.find((service) => service.id === form.serviceId), [form.serviceId, services]);

  useEffect(() => {
    if (!open) {
      queueMicrotask(() => {
        setError(null);
        setForm({
          patientId: '',
          professionalId: '',
          serviceId: '',
          boxId: '',
          date: format(new Date(), 'yyyy-MM-dd'),
          time: '09:00',
          notes: '',
        });
      });
      return;
    }

    if (form.professionalId && !filteredProfessionals.some((professional) => professional.id === form.professionalId)) {
      queueMicrotask(() => {
        setForm((current) => ({ ...current, professionalId: '' }));
      });
    }
  }, [filteredProfessionals, form.professionalId, open]);

  const updateField = <K extends keyof BookingFormValues>(key: K, value: BookingFormValues[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = () => {
    setError(null);

    if (!form.patientId || !form.professionalId || !form.serviceId || !form.date || !form.time) {
      setError('Paciente, profesional, servicio, fecha y hora son obligatorios.');
      return;
    }

    onSubmit(form);
  };

  return (
    <Modal open={open} title="Nueva cita" onClose={onClose}>
      <div className="space-y-5">
        {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Paciente</label>
            <select
              value={form.patientId}
              onChange={(event) => updateField('patientId', event.target.value)}
              className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Selecciona un paciente</option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.name}{patient.phone ? ` · ${patient.phone}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Servicio</label>
            <select
              value={form.serviceId}
              onChange={(event) => updateField('serviceId', event.target.value)}
              className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Selecciona un servicio</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} · {service.durationMinutes} min
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Profesional</label>
            <select
              value={form.professionalId}
              onChange={(event) => updateField('professionalId', event.target.value)}
              className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Selecciona un profesional</option>
              {filteredProfessionals.map((professional) => (
                <option key={professional.id} value={professional.id}>
                  {professional.user.name} · {professional.specialty}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Box (opcional)</label>
            <select
              value={form.boxId}
              onChange={(event) => updateField('boxId', event.target.value)}
              className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Asignar más tarde</option>
              {boxes.map((box) => (
                <option key={box.id} value={box.id}>{box.name}</option>
              ))}
            </select>
          </div>

          <Input label="Fecha" type="date" value={form.date} onChange={(event) => updateField('date', event.target.value)} />
          <Input label="Hora" type="time" value={form.time} onChange={(event) => updateField('time', event.target.value)} />
        </div>

        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          <p className="font-medium text-gray-900">Resumen</p>
          <p className="mt-1">Duración estimada: <strong>{selectedService?.durationMinutes ?? 0} min</strong></p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Notas</label>
          <textarea
            value={form.notes}
            onChange={(event) => updateField('notes', event.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Motivo de consulta, indicaciones o contexto adicional"
          />
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={isLoading}>Crear cita</Button>
        </div>
      </div>
    </Modal>
  );
}

type AppointmentStatus = 'pending' | 'confirmed' | 'arrived' | 'completed' | 'suspended' | 'cancelled' | 'no_show';

const STATUS_FLOW: { value: AppointmentStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Reservado', color: 'bg-yellow-500' },
  { value: 'confirmed', label: 'Confirmado', color: 'bg-blue-500' },
  { value: 'arrived', label: 'Llegó', color: 'bg-indigo-500' },
  { value: 'completed', label: 'Atendido', color: 'bg-green-500' },
];

function AppointmentDetailsModal({
  appointment,
  onClose,
  onStatusChange,
  isLoading,
}: {
  appointment: Appointment | null;
  onClose: () => void;
  onStatusChange: (status: AppointmentStatus) => void;
  isLoading: boolean;
}) {
  if (!appointment) return null;

  const date = appointment.startTime.split('T')[0];
  const currentIdx = STATUS_FLOW.findIndex((s) => s.value === appointment.status);
  const isFinal = ['completed', 'cancelled', 'no_show', 'suspended'].includes(appointment.status);

  return (
    <Modal open title="Información de la cita" onClose={onClose}>
      <div className="space-y-5">
        {/* Status badge + flow */}
        <div className="flex items-center justify-between">
          <Badge className={cn('text-xs', getStatusColor(appointment.status))}>{getStatusLabel(appointment.status)}</Badge>
          {!isFinal && (
            <div className="flex items-center gap-1">
              {STATUS_FLOW.map((step, i) => (
                <div key={step.value} className="flex items-center">
                  <div
                    className={cn(
                      'h-2.5 w-2.5 rounded-full',
                      i <= currentIdx ? step.color : 'bg-gray-200'
                    )}
                  />
                  {i < STATUS_FLOW.length - 1 && (
                    <div className={cn('h-0.5 w-4', i < currentIdx ? 'bg-gray-400' : 'bg-gray-200')} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Two-column info grid */}
        <div className="grid gap-x-8 gap-y-3 md:grid-cols-2 text-sm">
          {/* Left column: patient info */}
          <div className="space-y-3">
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Paciente</span>
              <p className="font-medium text-gray-900">{appointment.patient?.name ?? '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Teléfono</span>
              <p className="text-gray-700">{appointment.patient?.phone || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Correo</span>
              <p className="text-gray-700">{appointment.patient?.email || '—'}</p>
            </div>
            {appointment.patient?.rut && (
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wider">RUT</span>
                <p className="text-gray-700">{appointment.patient.rut}</p>
              </div>
            )}
          </div>

          {/* Right column: appointment info */}
          <div className="space-y-3">
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Fecha</span>
              <p className="text-gray-700">{date}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wider">Inicio</span>
                <p className="text-gray-700">{formatTime(appointment.startTime)}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wider">Fin</span>
                <p className="text-gray-700">{formatTime(appointment.endTime)}</p>
              </div>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Servicio</span>
              <p className="text-gray-700">{appointment.service?.name ?? '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Profesional</span>
              <p className="text-gray-700">{appointment.professional?.user?.name ?? '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider">Box</span>
              <p className="text-gray-700">{appointment.box?.name ?? 'Sin asignar'}</p>
            </div>
          </div>
        </div>

        {/* Notes */}
        {appointment.notes && (
          <div className="rounded-lg bg-gray-50 p-3">
            <span className="text-gray-500 text-xs uppercase tracking-wider">Comentario</span>
            <p className="mt-1 text-sm text-gray-700">{appointment.notes}</p>
          </div>
        )}

        {/* Status change buttons */}
        <div className="border-t pt-4">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2 block">Modificar estado</span>
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
            {appointment.status !== 'confirmed' && (
              <Button variant="outline" size="sm" onClick={() => onStatusChange('confirmed')} loading={isLoading}>
                Confirmar
              </Button>
            )}
            {appointment.status !== 'arrived' && !isFinal && (
              <Button variant="outline" size="sm" onClick={() => onStatusChange('arrived')} loading={isLoading}>
                Llegó
              </Button>
            )}
            {appointment.status !== 'completed' && (
              <Button variant="outline" size="sm" onClick={() => onStatusChange('completed')} loading={isLoading}>
                Atendido
              </Button>
            )}
            {appointment.status !== 'suspended' && !['completed', 'cancelled'].includes(appointment.status) && (
              <Button variant="outline" size="sm" className="text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => onStatusChange('suspended')} loading={isLoading}>
                Suspendió
              </Button>
            )}
            {appointment.status !== 'no_show' && !['completed', 'cancelled'].includes(appointment.status) && (
              <Button variant="outline" size="sm" className="text-gray-600 border-gray-200 hover:bg-gray-100" onClick={() => onStatusChange('no_show')} loading={isLoading}>
                No asistió
              </Button>
            )}
            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
              <Button variant="destructive" size="sm" onClick={() => onStatusChange('cancelled')} loading={isLoading}>
                Cancelar
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function combineDateAndTime(date: string, time: string) {
  return new Date(`${date}T${time}:00`).toISOString();
}

function buildHalfHourLabels(startHour: number, endHour: number) {
  const labels: string[] = [];
  for (let hour = startHour; hour <= endHour; hour += 1) {
    labels.push(`${String(hour).padStart(2, '0')}:00`);
    labels.push(`${String(hour).padStart(2, '0')}:30`);
  }
  return labels;
}

function getHalfHourLabel(dateTime: string) {
  const d = new Date(dateTime);
  const hour = String(d.getHours()).padStart(2, '0');
  const minute = d.getMinutes();
  return `${hour}:${minute < 30 ? '00' : '30'}`;
}

export function CalendarPage() {
  const queryClient = useQueryClient();
  const { clinic, user } = useAuthStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedProfessional, setSelectedProfessional] = useState<string>('all');
  const [selectedBox, setSelectedBox] = useState<string>('all');
  const [showCancelled, setShowCancelled] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);

  const { data: professionalsData } = useQuery({
    queryKey: ['professionals', clinic?.id, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabaseProfessionals(clinic.id, true);
      }

      const response = await professionalsApi.list(true);
      return (response.data as Professional[]) || [];
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  const { data: boxesData } = useQuery({
    queryKey: ['boxes', clinic?.id, 'calendar', isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabaseBoxes(clinic.id, true);
      }

      const response = await boxesApi.list();
      return ((response.data as Box[]) || []).filter((box) => box.isActive);
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  const { data: patientsData } = useQuery({
    queryKey: ['patients', clinic?.id, 'calendar-form', isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabasePatientsList(clinic.id);
      }

      const response = await patientsApi.list({ limit: 200 });
      return ((response.data as Array<Record<string, unknown>>) || []).map((patient) => ({
        id: String(patient.id),
        name: String(patient.name ?? ''),
        phone: patient.phone ? String(patient.phone) : '',
        email: patient.email ? String(patient.email) : undefined,
      }));
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  const { data: servicesData } = useQuery({
    queryKey: ['services', clinic?.id, 'calendar-form', isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabaseServices(clinic.id);
      }

      const response = await servicesApi.list(true);
      return ((response.data as Array<Record<string, unknown>>) || []).map((service) => ({
        id: String(service.id),
        name: String(service.name ?? 'Servicio'),
        durationMinutes: Number(service.durationMinutes ?? 0),
        price: Number(service.price ?? 0),
      }));
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  const professionals = professionalsData || [];
  const allBoxes = boxesData || [];
  const patients = patientsData || [];
  const services = servicesData || [];
  const activeBoxes = allBoxes.filter((box) => box.isActive);
  const timeLabels = useMemo(() => buildHalfHourLabels(8, 20), []);

  const { data: appointmentsData, isLoading } = useQuery({
    queryKey: ['appointments', clinic?.id, format(weekStart, 'yyyy-MM-dd'), format(weekEnd, 'yyyy-MM-dd'), selectedProfessional, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      const startDate = format(weekStart, 'yyyy-MM-dd');
      const endDate = format(weekEnd, 'yyyy-MM-dd');

      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabaseAppointments(clinic.id, {
          startDate,
          endDate,
          professionalId: selectedProfessional !== 'all' ? selectedProfessional : undefined,
        });
      }

      const response = await appointmentsApi.list({
        startDate,
        endDate,
        professionalId: selectedProfessional !== 'all' ? selectedProfessional : undefined,
        limit: 200,
      });

      return (response.data as Appointment[]) || [];
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // Smart sync: lightweight polling that only re-fetches when changes are detected
  const appointmentsQueryKey = ['appointments', clinic?.id, format(weekStart, 'yyyy-MM-dd'), format(weekEnd, 'yyyy-MM-dd'), selectedProfessional, isSupabaseAuthEnabled ? 'supabase' : 'legacy'];
  const { lastSynced, isSyncing, syncNow } = useSmartSync({
    queryKey: appointmentsQueryKey,
    startDate: format(weekStart, 'yyyy-MM-dd'),
    endDate: format(weekEnd, 'yyyy-MM-dd'),
    professionalId: selectedProfessional !== 'all' ? selectedProfessional : undefined,
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  // Track new appointments for highlight animation
  const prevAppointmentIdsRef = useRef<Set<string>>(new Set());
  const newAppointmentIds = useMemo(() => {
    const currentIds = new Set((appointmentsData || []).map((a) => a.id));
    const newIds = new Set<string>();
    // Skip highlight on first render (when prev is empty)
    if (prevAppointmentIdsRef.current.size > 0) {
      for (const id of currentIds) {
        if (!prevAppointmentIdsRef.current.has(id)) {
          newIds.add(id);
        }
      }
    }
    prevAppointmentIdsRef.current = currentIds;
    return newIds;
  }, [appointmentsData]);

  const createMutation = useMutation({
    mutationFn: async (values: BookingFormValues) => {
      const service = services.find((item) => item.id === values.serviceId);

      if (!service) {
        throw new Error('Debes seleccionar un servicio válido.');
      }

      const startTime = combineDateAndTime(values.date, values.time);
      const endTime = addMinutes(new Date(startTime), service.durationMinutes).toISOString();

      if (isSupabaseAuthEnabled) {
        if (!clinic?.id) {
          throw new Error('No hay una clínica activa disponible.');
        }

        await createSupabaseAppointment(clinic.id, {
          patientId: values.patientId,
          professionalId: values.professionalId,
          serviceId: values.serviceId,
          startTime,
          endTime,
          notes: values.notes,
          boxId: values.boxId || null,
        });
        return;
      }

      await appointmentsApi.create({
        patientId: values.patientId,
        professionalId: values.professionalId,
        serviceId: values.serviceId,
        startTime,
        endTime,
        notes: values.notes || undefined,
      });
    },
    onSuccess: () => {
      setShowCreateModal(false);
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: AppointmentStatus }) => {
      if (isSupabaseAuthEnabled) {
        await updateSupabaseAppointment(id, { status: status as 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show' });
        return;
      }

      await appointmentsApi.update(id, { status });
    },
    onSuccess: () => {
      setSelectedAppointment(null);
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const appointmentsByDayAndSlot = useMemo(() => {
    const allAppointments = appointmentsData || [];
    let appointments = showCancelled ? allAppointments : allAppointments.filter((appointment) => appointment.status !== 'cancelled');

    if (selectedBox !== 'all') {
      if (selectedBox === 'none') {
        appointments = appointments.filter((appointment) => !appointment.box);
      } else {
        appointments = appointments.filter((appointment) => appointment.box?.id === selectedBox);
      }
    }

    const grouped: Record<string, Record<string, Appointment[]>> = {};
    weekDays.forEach((day) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      grouped[dateKey] = {};
      timeLabels.forEach((timeLabel) => {
        grouped[dateKey][timeLabel] = [];
      });
    });

    appointments.forEach((appointment) => {
      const dateKey = appointment.startTime.split('T')[0];
      const timeKey = getHalfHourLabel(appointment.startTime);
      if (grouped[dateKey]?.[timeKey]) {
        grouped[dateKey][timeKey].push(appointment);
      }
    });

    Object.keys(grouped).forEach((dateKey) => {
      Object.keys(grouped[dateKey]).forEach((timeKey) => {
        grouped[dateKey][timeKey].sort((left, right) => left.startTime.localeCompare(right.startTime));
      });
    });

    return grouped;
  }, [appointmentsData, selectedBox, showCancelled, timeLabels, weekDays]);

  const cancelledCount = useMemo(() => {
    const allAppointments = appointmentsData || [];
    return allAppointments.filter((appointment) => appointment.status === 'cancelled').length;
  }, [appointmentsData]);

  const goToPreviousWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const goToNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  useEffect(() => {
    if (user?.role === 'admin') {
      setSelectedProfessional('all');
      setSelectedBox('all');
    }
  }, [user?.role]);

  const boxColors: Record<string, string> = {};
  const palette = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];
  activeBoxes.forEach((box, index) => {
    boxColors[box.id] = palette[index % palette.length];
  });

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calendario</h1>
            <p className="text-gray-600">
              {format(weekStart, "d 'de' MMMM", { locale: es })} - {format(weekEnd, "d 'de' MMMM, yyyy", { locale: es })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SyncIndicator lastSynced={lastSynced} isSyncing={isSyncing} onRefresh={syncNow} />
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="mr-2 h-4 w-4" />Nueva cita
            </Button>
          </div>
        </div>

        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPreviousWeek}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={goToToday}>Hoy</Button>
            <Button variant="outline" size="icon" onClick={goToNextWeek}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {cancelledCount > 0 && (
              <button
                onClick={() => setShowCancelled(!showCancelled)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  showCancelled ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                )}
              >
                {showCancelled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                {showCancelled ? 'Ocultar' : 'Mostrar'} canceladas ({cancelledCount})
              </button>
            )}
            <select value={selectedBox} onChange={(event) => setSelectedBox(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              <option value="all">Todos los boxes</option>
              {activeBoxes.map((box) => <option key={box.id} value={box.id}>{box.name}</option>)}
              <option value="none">Sin box asignado</option>
            </select>
            <select value={selectedProfessional} onChange={(event) => setSelectedProfessional(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              <option value="all">Todos los profesionales</option>
              {professionals.map((professional) => (
                <option key={professional.id} value={professional.id}>{professional.user.name} - {professional.specialty}</option>
              ))}
            </select>
          </div>
        </div>

        {activeBoxes.length > 0 && selectedBox === 'all' && (
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Boxes:</span>
            {activeBoxes.map((box) => (
              <button
                key={box.id}
                onClick={() => setSelectedBox(box.id)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors hover:bg-gray-100"
              >
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: boxColors[box.id] }} />
                {box.name}
              </button>
            ))}
          </div>
        )}

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <div className="grid min-w-[980px]" style={{ gridTemplateColumns: '62px repeat(7, minmax(120px, 1fr))' }}>
              <div className="border-b border-r bg-gray-50" />
              {weekDays.map((day) => (
                <div key={day.toISOString()} className={cn('border-b border-r px-1 py-2 text-center last:border-r-0', isSameDay(day, new Date()) && 'bg-primary/5')}>
                  <p className="text-[10px] uppercase text-gray-500">{format(day, 'EEE', { locale: es })}</p>
                  <p className={cn('text-sm font-semibold', isSameDay(day, new Date()) && 'text-primary')}>{format(day, 'd')}</p>
                </div>
              ))}

              {timeLabels.map((timeLabel) => (
                <Fragment key={`row-${timeLabel}`}>
                  <div className="border-b border-r bg-gray-50 px-1 py-1 text-right text-[10px] font-medium text-gray-500">
                    {timeLabel}
                  </div>
                  {weekDays.map((day) => {
                    const dateKey = format(day, 'yyyy-MM-dd');
                    const slotAppointments = appointmentsByDayAndSlot[dateKey]?.[timeLabel] || [];

                    return (
                      <div key={`${dateKey}-${timeLabel}`} className={cn('min-h-[34px] border-b border-r p-0.5 last:border-r-0', isSameDay(day, new Date()) && 'bg-primary/5')}>
                        {isLoading ? (
                          <div className="h-5 animate-pulse rounded bg-gray-100" />
                        ) : (
                          <div className="space-y-0.5">
                            {slotAppointments.map((appointment) => {
                              const isCancelled = appointment.status === 'cancelled';

                              return (
                                <button
                                  key={appointment.id}
                                  type="button"
                                  onClick={() => setSelectedAppointment(appointment)}
                                  className={cn(
                                    'w-full rounded-sm px-1 py-0.5 text-left text-[10px] leading-tight transition-opacity',
                                    isCancelled ? 'border-l-[3px] border-gray-300 bg-gray-50 opacity-40 hover:opacity-60' : 'hover:opacity-80',
                                    newAppointmentIds.has(appointment.id) && 'animate-highlight-new'
                                  )}
                                  style={isCancelled ? undefined : {
                                    backgroundColor: `${appointment.professional?.color || '#6366f1'}20`,
                                    borderLeft: `3px solid ${appointment.professional?.color || '#6366f1'}`,
                                  }}
                                >
                                  <p className={cn('truncate text-gray-700', isCancelled && 'line-through text-gray-400')}>
                                    <span className="mr-1 font-medium">{formatTime(appointment.startTime)}</span>
                                    <span>{appointment.patient?.name}</span>
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <BookingModal
        open={showCreateModal}
        patients={patients}
        professionals={professionals}
        services={services}
        boxes={activeBoxes}
        onClose={() => setShowCreateModal(false)}
        onSubmit={(values) => createMutation.mutate(values)}
        isLoading={createMutation.isPending}
      />

      <AppointmentDetailsModal
        appointment={selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        onStatusChange={(status) => {
          if (!selectedAppointment) {
            return;
          }

          statusMutation.mutate({ id: selectedAppointment.id, status });
        }}
        isLoading={statusMutation.isPending}
      />
    </>
  );
}
